/* Серверный источник шаблонных «Настроек»: значения, сохранение с версией (If-Match), «Что нового», очистка истории, выгрузка и удаление аккаунта.
   Ключи идемпотентности и начатые операции живут в sessionStorage: перезагрузка не запускает операцию второй раз. */
import { endpoints } from '../../api';
import { helpRoute, navigateHelp } from '../help/help-navigation';
import { ApiError, uuid } from '../../api/client';
import type { AccountErasure, AccountExport, NewsRelease, NotificationFlags, SettingsPatch, SettingsResponse } from '../../api/account';
import { readAppearance, saveAppearance, type Appearance } from './appearance';
import { applyRuntimePreferences } from './runtime-preferences';

/** Порядок сегмента темы шаблона: как в системе, светлая, тёмная */
const THEMES: Appearance[] = ['system', 'light', 'dark'];

export interface NewsGroup {
  title: string;
  items: { id: string; title: string; text: string; screen: string | null }[];
}

export interface SettingsNewsView {
  version: string | null;
  date: string | null;
  summary: string | null;
  groups: NewsGroup[];
}

/** Карточка из строки: первое предложение — заголовок, остальное — текст. id — версия и место в релизе (у строки своего нет) */
function textItem(text: string, version: string, g: number, i: number) {
  const cut = text.search(/[.!?](\s|$)/);
  const title = cut > 0 ? text.slice(0, cut) : text;
  return { id: `${version}-${g + 1}-${i + 1}`, title, text: cut > 0 ? text.slice(cut + 1).trim() : '', screen: null };
}

const EXPORT_KEY = 'steos:export:key';
const EXPORT_OP = 'steos:export:operation';
const ERASE_KEY = 'steos:erasure:key';
const ERASE_OP = 'steos:erasure:operation';
const CLEAR_KEY = 'steos:history:clear-key';
const DONE = ['done', 'partial', 'failed', 'expired'];

function ownerKey(key: string) {
  const owner = (window as unknown as { BotServerMe?: { user?: { id?: number } } }).BotServerMe?.user?.id;
  return `${key}:owner:${owner ?? 'unbound'}`;
}

function read<T>(key: string): T | null {
  try {
    return JSON.parse(sessionStorage.getItem(ownerKey(key)) || 'null') as T | null;
  } catch {
    return null;
  }
}
function write(key: string, value: unknown, storageKey = ownerKey(key)) {
  if (value === null) sessionStorage.removeItem(storageKey);
  else sessionStorage.setItem(storageKey, JSON.stringify(value));
}
/** Ключ операции: тот же до явного завершения, чтобы повтор после обрыва не создал вторую */
function keyOf(name: string) {
  const known = read<string>(name);
  if (known) return known;
  const key = uuid();
  write(name, key);
  return key;
}

function news(release: NewsRelease): SettingsNewsView {
  const groups = release.groups ?? (release.items ? [{ id: 'all', title: 'Обновления', items: release.items }] : []);
  const version = release.version ?? 'news';
  return {
    version: release.version ?? null,
    date: release.date ?? null,
    summary: release.summary ?? null,
    groups: groups
      .map((g, gi) => ({
        title: g.title,
        items: g.items
          .map((i, ii) => typeof i === 'string' ? textItem(i, version, gi, ii) : { id: i.id, title: i.title, text: i.text ?? i.description ?? '', screen: i.screen ?? null })
          .filter(i => i.id && i.title),
      }))
      .filter(g => g.items.length),
  };
}

export const settingsServer = {
  async load(): Promise<SettingsResponse> {
    const value = await endpoints.account.settings();
    applyRuntimePreferences(value);
    return value;
  },
  /** Тема хранится на устройстве: сервер её не знает */
  theme: () => Math.max(0, THEMES.indexOf(readAppearance())),
  setTheme: (i: number) => saveAppearance(THEMES[i] ?? 'system'),
  historyStats: () => endpoints.history.stats(),
  canPrefs: (s: SettingsResponse) => !!s.capabilities.voice_preferences,
  canNotify: (s: SettingsResponse) => !!s.capabilities.notifications_write,
  canWrite: (s: SettingsResponse) => !!s.capabilities.product_settings_write && !!s.sources.product_settings?.version,
  flags: (s: SettingsResponse) => (s.capabilities.notifications_read && s.notifications ? (s.notifications as NotificationFlags) : null),
  async saveProduct(s: SettingsResponse, patch: SettingsPatch): Promise<SettingsResponse> {
    const version = s.sources.product_settings?.version;
    if (!version || !s.capabilities.product_settings_write) throw new Error('settings_read_only');
    const value = await endpoints.account.saveSettings(version, patch);
    applyRuntimePreferences(value);
    return value;
  },
  async saveNotifications(s: SettingsResponse, flags: NotificationFlags): Promise<SettingsResponse> {
    const version = s.notifications_version ?? s.sources.bot_notifications?.version;
    if (!version || !s.capabilities.notifications_write) throw new Error('settings_read_only');
    const r = await endpoints.account.saveNotifications(version, flags);
    return { ...s, notifications: r.notifications, notifications_version: r.notifications_version, sources: { ...s.sources, bot_notifications: { version: r.notifications_version } } };
  },
  async savePrefs(s: SettingsResponse, changes: { lang?: string; currency?: string }): Promise<SettingsResponse> {
    if (!settingsServer.canPrefs(s) || !s.sources.voice_preferences?.version) throw new Error('settings_read_only');
    await endpoints.account.savePreferences(s.sources.voice_preferences.version, changes);
    return settingsServer.load();
  },
  /** Чужая версия: показываем актуальное, изменение не повторяем */
  conflict: (e: unknown) => e instanceof ApiError && (e.status === 412 || e.status === 409),
  error(e: unknown) {
    if (e instanceof Error && e.message === 'settings_read_only') return 'Сохранение настроек сейчас недоступно';
    if (settingsServer.conflict(e)) return 'Настройки уже изменились, показываем актуальные';
    return 'Не удалось сохранить. Попробуйте ещё раз';
  },
  newsTarget: (screen: unknown) => helpRoute(screen),
  navigateNews: (screen: string) => navigateHelp(screen),
  async news(): Promise<SettingsNewsView> {
    return news(await endpoints.account.news());
  },
  async clearHistory() {
    const storageKey = ownerKey(CLEAR_KEY);
    const result = await endpoints.account.clearHistory(keyOf(CLEAR_KEY));
    if (result.cleared !== true) throw new Error('history_clear_unconfirmed');
    write(CLEAR_KEY, null, storageKey);
    return result;
  },
  exportScope(e: AccountExport) {
    const names: Record<string, string> = { voice: 'Настройки, ресурсы и история', backend: 'Данные Mini App', identity: 'Учётная запись и финансовые данные STEOS ID', bot: 'Переписка с ботом', binary_media: 'Фото, аудио, видео и модели', platform_products_outside_miniapp: 'Данные других продуктов STEOS' };
    const label = (key: string) => names[key] ?? key;
    return {
      unavailable: (e.manifest?.sources ?? []).filter(s => s.status === 'unavailable').map(s => label(s.provider)),
      partial: (e.manifest?.sources ?? []).filter(s => s.status === 'partial' || s.status === 'truncated').map(s => label(s.provider)),
      excluded: (e.manifest?.excluded ?? []).map(label),
    };
  },
  exportNow: () => read<AccountExport>(EXPORT_OP),
  exportDone: (e: AccountExport | null) => !!e && DONE.includes(e.status),
  async exportStart() {
    const storageKey = ownerKey(EXPORT_OP);
    const value = await endpoints.account.createExport(keyOf(EXPORT_KEY));
    write(EXPORT_OP, value, storageKey);
    return value;
  },
  async exportPoll(id: string) {
    const storageKey = ownerKey(EXPORT_OP);
    const value = await endpoints.account.exportStatus(id);
    write(EXPORT_OP, value, storageKey);
    return value;
  },
  exportDownload: (id: string) => endpoints.account.exportDownload(id),
  /** Новая выгрузка после завершённой: прежний ключ и операция забываются */
  exportReset() {
    write(EXPORT_KEY, null);
    write(EXPORT_OP, null);
  },
  erasureDone: (e: AccountErasure | null) => !!e && ['completed', 'failed', 'partial', 'manual_review'].includes(e.status),
  erasureNow: () => read<AccountErasure>(ERASE_OP),
  async erase() {
    const storageKey = ownerKey(ERASE_OP);
    const value = await endpoints.account.erase(keyOf(ERASE_KEY));
    write(ERASE_OP, value, storageKey);
    return value;
  },
  async erasurePoll(e: AccountErasure) {
    const storageKey = ownerKey(ERASE_OP);
    if (!e.status_receipt) return e;
    const value = { ...(await endpoints.account.erasureStatus(e.operation_id, e.status_receipt)), status_receipt: e.status_receipt };
    write(ERASE_OP, value, storageKey);
    return value;
  },
};

export type SettingsServer = typeof settingsServer;
